import { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader } from 'lucide-react';

import { incidentsApiService } from '../../services/incidentsApi';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';
import type { IncidentWatcher } from '../../types/incident';

export interface IncidentWatcherButtonProps {
  incidentId: string;
  /** Bumped by parent on `incident.watcher_changed` WS events. */
  refreshNonce?: number;
  /** Optional callback so the parent can update an aggregate watcher count. */
  onWatchersChanged?: (watchers: IncidentWatcher[]) => void;
}

/**
 * Toggle button for self-watching an incident. Watching subscribes the caller to
 * the hourly digest email for that incident.
 *
 * Renders a compact pill: "👁 Watching · N" or "👁 Watch · N" depending on state.
 */
export const IncidentWatcherButton = ({
  incidentId,
  refreshNonce = 0,
  onWatchersChanged,
}: IncidentWatcherButtonProps) => {
  const { identity } = useAuth();
  const myUuid = identity?.raw?.custom?.minecraft_uuid;
  const myEmail = identity?.email;

  const [watchers, setWatchers] = useState<IncidentWatcher[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isWatching =
    !!myUuid && (watchers ?? []).some((w) => w.user_uuid === myUuid);
  const count = watchers?.length ?? 0;

  const load = async () => {
    if (!incidentId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await incidentsApiService.listWatchers(incidentId);
      setWatchers(list);
      onWatchersChanged?.(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load watchers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  const toggle = async () => {
    if (!myUuid) return;
    setBusy(true);
    setError(null);
    try {
      if (isWatching) {
        await incidentsApiService.unwatchIncident(incidentId);
      } else {
        await incidentsApiService.watchIncident(incidentId);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle watch');
    } finally {
      setBusy(false);
    }
  };

  // No identity / no email → render a non-interactive count-only chip.
  if (!myUuid) {
    return (
      <span
        className="glass-button-secondary !cursor-default tabular-nums"
        title="Sign-in required to watch"
      >
        <Eye className="w-4 h-4" />
        {loading ? '…' : count}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={busy || loading || !myEmail}
      title={
        myEmail
          ? isWatching
            ? 'Stop watching — you will no longer receive hourly digest emails for this incident.'
            : 'Watch this incident — receive an hourly digest email summarising activity.'
          : 'Cannot watch: no email associated with your identity.'
      }
      aria-pressed={isWatching}
      className={cn(
        isWatching
          ? 'glass-button-secondary !text-purple-300 !border-purple-500/30 !bg-purple-500/15'
          : 'glass-button-secondary',
        (busy || loading || !myEmail) && 'opacity-60 cursor-not-allowed',
      )}
    >
      {busy || loading ? (
        <Loader className="w-4 h-4 animate-spin" />
      ) : isWatching ? (
        <Eye className="w-4 h-4" />
      ) : (
        <EyeOff className="w-4 h-4" />
      )}
      <span>{isWatching ? 'Watching' : 'Watch'}</span>
      <span className="text-xs text-glass-400 tabular-nums">{count}</span>
      {error && <span className="sr-only">{error}</span>}
    </button>
  );
};
